// src/pages/CadastroDetalhesScreen.jsx
// Etapa de perfil profissional + confirmação de e-mail antes do pagamento.

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  CheckCircle2,
  CreditCard,
  LoaderCircle,
  LockKeyhole,
  Mail,
  MailCheck,
  MapPin,
  Phone,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import "../sass/pages/cadastroDetalhes.scss";

import Logo from "../assets/logoClaro.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const STORAGE_KEYS = {
  TEMP_SESSION_ID: "psy_temp_register_session_id",
  TEMP_EMAIL: "psy_temp_register_email",
  PLAN_NAME: "psy_temp_register_plan_name",
  PLAN_PRICE: "psy_temp_register_plan_price",
  TIPO_USUARIO: "psy_temp_register_tipo_usuario",
  PRICE_ID: "psy_temp_register_price_id",
  REGISTRATION_FLOW: "psyassist:registration-flow",
  SELECTED_PLAN: "psyassist:selected-plan",
};

const EMPTY_FLOW = {
  sessionId: "",
  email: "",
  planName: "Plano não identificado",
  planPrice: "Valor não informado",
  tipoUsuario: "individual",
  priceId: "",
};

const normalizeDigits = (value = "") =>
  String(value || "").replace(/\D/g, "");

const safeParseJson = (value, fallback = null) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, String(value ?? ""));
    return true;
  } catch {
    return false;
  }
};

const formatCrpForInput = (value) => {
  const digits = normalizeDigits(value).slice(0, 8);

  if (digits.length <= 2) return digits;

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatCrpForPayload = (value) => {
  const digits = normalizeDigits(value);

  if (digits.length !== 8) return String(value || "").trim();

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatCepInput = (value) => {
  const digits = normalizeDigits(value).slice(0, 8);

  if (digits.length <= 5) return digits;

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatPhoneInput = (value) => {
  const digits = normalizeDigits(value).slice(0, 11);

  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }

  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const formatDateInput = (value) => {
  const digits = normalizeDigits(value).slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidDateBR = (value) => {
  const match = String(value || "").match(
    /^(\d{2})\/(\d{2})\/(\d{4})$/
  );

  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date.getTime() <= Date.now()
  );
};

const normalizeCodigo = (value) =>
  normalizeDigits(value).slice(0, 6);

const getErrorMessage = (
  error,
  fallback = "Não foi possível concluir a operação."
) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

const normalizeFlow = (source = {}) => {
  const tipoUsuario =
    source.tipoUsuario === "clinica" ? "clinica" : "individual";

  const planPrice =
    tipoUsuario === "clinica"
      ? source.planPriceClinica ||
        source.planPrice ||
        source.valorPlano ||
        source.planPriceIndividual
      : source.planPriceIndividual ||
        source.planPrice ||
        source.valorPlano ||
        source.planPriceClinica;

  return {
    sessionId: String(source.sessionId || "").trim(),
    email: String(source.email || "").trim().toLowerCase(),
    planName: String(
      source.planName ||
        source.plano ||
        EMPTY_FLOW.planName
    ).trim(),
    planPrice: String(planPrice || EMPTY_FLOW.planPrice).trim(),
    tipoUsuario,
    priceId: String(source.priceId || "").trim(),
    nome: String(source.nome || source.nomeUsuario || "").trim(),
    crp: String(source.crp || "").trim(),
    telefone: String(source.telefone || "").trim(),
    nascimento: String(source.nascimento || "").trim(),
    cep: String(source.cep || "").trim(),
    endereco: String(source.endereco || "").trim(),
    bairro: String(source.bairro || "").trim(),
    cidade: String(source.cidade || "").trim(),
    uf: String(source.uf || source.estado || "").trim().toUpperCase(),
    pais: String(source.pais || "Brasil").trim(),
  };
};

function FormField({
  id,
  label,
  hint,
  error,
  required = false,
  className = "",
  children,
}) {
  return (
    <div
      className={`cadastro-detalhes-field ${
        error ? "cadastro-detalhes-field--error" : ""
      } ${className}`}
    >
      <div className="cadastro-detalhes-field__header">
        <label htmlFor={id}>
          {label}
          {required ? <span aria-hidden="true">*</span> : null}
        </label>

        {hint ? <small>{hint}</small> : null}
      </div>

      {children}

      {error ? (
        <p className="cadastro-detalhes-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function OtpInput({
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  const inputRefs = useRef([]);
  const digits = Array.from(
    { length: 6 },
    (_, index) => value[index] || ""
  );

  const updateFromIndex = (index, rawValue) => {
    const incoming = normalizeDigits(rawValue);

    if (!incoming) {
      const next = [...digits];
      next[index] = "";
      onChange(next.join(""));
      return;
    }

    const next = [...digits];

    incoming
      .slice(0, 6 - index)
      .split("")
      .forEach((digit, offset) => {
        next[index + offset] = digit;
      });

    onChange(next.join(""));

    const nextIndex = Math.min(index + incoming.length, 5);
    window.requestAnimationFrame(() => {
      inputRefs.current[nextIndex]?.focus();
      inputRefs.current[nextIndex]?.select();
    });
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      inputRefs.current[index - 1]?.select();
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = normalizeCodigo(
      event.clipboardData.getData("text")
    );

    if (!pasted) return;

    event.preventDefault();
    onChange(pasted);

    window.requestAnimationFrame(() => {
      const index = Math.min(pasted.length, 6) - 1;
      inputRefs.current[Math.max(index, 0)]?.focus();
    });
  };

  return (
    <div
      className={`cadastro-detalhes-otp ${
        error ? "cadastro-detalhes-otp--error" : ""
      }`}
      onPaste={handlePaste}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          onChange={(event) =>
            updateFromIndex(index, event.target.value)
          }
          onKeyDown={(event) => handleKeyDown(event, index)}
          onFocus={(event) => event.target.select()}
          disabled={disabled}
          aria-label={`Dígito ${index + 1} do código`}
          aria-invalid={Boolean(error)}
        />
      ))}
    </div>
  );
}

const CadastroDetalhesScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [nome, setNome] = useState("");
  const [crp, setCrp] = useState("");
  const [cep, setCep] = useState("");
  const [endereco, setEndereco] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [pais, setPais] = useState("Brasil");
  const [telefone, setTelefone] = useState("");
  const [nascimento, setNascimento] = useState("");

  const [tipoUsuario, setTipoUsuario] = useState("individual");
  const [planName, setPlanName] = useState(EMPTY_FLOW.planName);
  const [planPrice, setPlanPrice] = useState(EMPTY_FLOW.planPrice);
  const [sessionId, setSessionId] = useState("");
  const [priceId, setPriceId] = useState("");
  const [email, setEmail] = useState("");

  const [etapa, setEtapa] = useState("detalhes");
  const [codigoEmail, setCodigoEmail] = useState("");
  const [errors, setErrors] = useState({});

  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepEncontrado, setCepEncontrado] = useState(false);
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [confirmandoCodigo, setConfirmandoCodigo] = useState(false);
  const [salvandoDetalhes, setSalvandoDetalhes] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [flowLoaded, setFlowLoaded] = useState(false);

  const lastCepRequestedRef = useRef("");

  useEffect(() => {
    const navigationState =
      location.state && typeof location.state === "object"
        ? location.state
        : {};

    const sessionFlow =
      safeParseJson(
        readStorage(sessionStorage, STORAGE_KEYS.REGISTRATION_FLOW),
        null
      ) ||
      safeParseJson(
        readStorage(sessionStorage, STORAGE_KEYS.SELECTED_PLAN),
        null
      ) ||
      {};

    const legacyFlow = {
      sessionId: readStorage(
        localStorage,
        STORAGE_KEYS.TEMP_SESSION_ID
      ),
      email: readStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL),
      planName: readStorage(localStorage, STORAGE_KEYS.PLAN_NAME),
      planPrice: readStorage(localStorage, STORAGE_KEYS.PLAN_PRICE),
      tipoUsuario: readStorage(
        localStorage,
        STORAGE_KEYS.TIPO_USUARIO
      ),
      priceId: readStorage(localStorage, STORAGE_KEYS.PRICE_ID),
    };

    const normalized = normalizeFlow({
      ...legacyFlow,
      ...sessionFlow,
      ...navigationState,
    });

    setSessionId(normalized.sessionId);
    setEmail(normalized.email);
    setPlanName(normalized.planName);
    setPlanPrice(normalized.planPrice);
    setTipoUsuario(normalized.tipoUsuario);
    setPriceId(normalized.priceId);

    setNome(normalized.nome);
    setCrp(formatCrpForInput(normalized.crp));
    setTelefone(formatPhoneInput(normalized.telefone));
    setNascimento(formatDateInput(normalized.nascimento));
    setCep(formatCepInput(normalized.cep));
    setEndereco(normalized.endereco);
    setBairro(normalized.bairro);
    setCidade(normalized.cidade);
    setUf(normalized.uf);
    setPais(normalized.pais || "Brasil");

    setFlowLoaded(true);
  }, [location.state]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const intervalId = window.setInterval(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendCooldown]);

  const flowValid = Boolean(sessionId && email);

  const userTypeLabel =
    tipoUsuario === "clinica" ? "Conta clínica" : "Conta profissional";

  const progressValue = etapa === "confirmarEmail" ? 88 : 68;

  const loading =
    salvandoDetalhes ||
    enviandoCodigo ||
    confirmandoCodigo;

  const clearFieldError = (field) => {
    setErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const persistRegistrationFlow = useCallback(
    (extra = {}) => {
      const flow = {
        sessionId,
        email,
        planName,
        planPrice,
        tipoUsuario,
        priceId,
        nome: nome.trim(),
        crp: formatCrpForPayload(crp),
        telefone,
        nascimento,
        cep,
        endereco: endereco.trim(),
        bairro: bairro.trim(),
        cidade: cidade.trim(),
        uf: uf.trim().toUpperCase(),
        pais: pais.trim(),
        ...extra,
      };

      try {
        sessionStorage.setItem(
          STORAGE_KEYS.REGISTRATION_FLOW,
          JSON.stringify(flow)
        );
      } catch {
        // O state de navegação continua sendo utilizado.
      }

      writeStorage(
        localStorage,
        STORAGE_KEYS.TEMP_SESSION_ID,
        sessionId
      );
      writeStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL, email);
      writeStorage(localStorage, STORAGE_KEYS.PLAN_NAME, planName);
      writeStorage(localStorage, STORAGE_KEYS.PLAN_PRICE, planPrice);
      writeStorage(
        localStorage,
        STORAGE_KEYS.TIPO_USUARIO,
        tipoUsuario
      );
      writeStorage(localStorage, STORAGE_KEYS.PRICE_ID, priceId);

      return flow;
    },
    [
      bairro,
      cep,
      cidade,
      crp,
      email,
      endereco,
      nascimento,
      nome,
      pais,
      planName,
      planPrice,
      priceId,
      sessionId,
      telefone,
      tipoUsuario,
      uf,
    ]
  );

  const buscarEnderecoPorCep = useCallback(
    async (rawCep) => {
      const digits = normalizeDigits(rawCep);

      if (digits.length !== 8) return;
      if (lastCepRequestedRef.current === digits) return;

      lastCepRequestedRef.current = digits;
      setBuscandoCep(true);
      setCepEncontrado(false);

      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${digits}/json/`,
          {
            timeout: 8000,
          }
        );

        if (response.status === 200 && !response.data?.erro) {
          setEndereco(response.data?.logradouro || "");
          setBairro(response.data?.bairro || "");
          setCidade(response.data?.localidade || "");
          setUf(String(response.data?.uf || "").toUpperCase());
          setCepEncontrado(true);
          clearFieldError("cep");
          return;
        }

        setErrors((current) => ({
          ...current,
          cep: "CEP não localizado. Revise o número informado.",
        }));
      } catch (error) {
        console.error("Erro ao consultar CEP:", error);

        toast.warning({
          title: "Consulta de CEP indisponível",
          message:
            "Você pode continuar preenchendo o endereço manualmente.",
        });
      } finally {
        setBuscandoCep(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    const digits = normalizeDigits(cep);

    if (digits.length !== 8) {
      lastCepRequestedRef.current = "";
      setCepEncontrado(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      buscarEnderecoPorCep(digits);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [buscarEnderecoPorCep, cep]);

  const validateDetails = () => {
    const nextErrors = {};
    const nomeTrim = nome.trim();
    const crpDigits = normalizeDigits(crp);
    const cepDigits = normalizeDigits(cep);
    const phoneDigits = normalizeDigits(telefone);

    if (!nomeTrim) {
      nextErrors.nome = "Informe seu nome completo.";
    } else if (nomeTrim.length < 3) {
      nextErrors.nome = "O nome informado está muito curto.";
    }

    if (crpDigits.length !== 8) {
      nextErrors.crp = "Informe o CRP no formato 00/000000.";
    }

    if (!email) {
      nextErrors.email =
        "O e-mail da etapa anterior não foi localizado.";
    }

    if (telefone && ![10, 11].includes(phoneDigits.length)) {
      nextErrors.telefone =
        "Informe um telefone com DDD válido.";
    }

    if (nascimento && !isValidDateBR(nascimento)) {
      nextErrors.nascimento =
        "Informe uma data válida no formato dd/mm/aaaa.";
    }

    if (cepDigits.length !== 8) {
      nextErrors.cep = "Informe um CEP com 8 dígitos.";
    }

    if (uf && uf.trim().length !== 2) {
      nextErrors.uf = "Informe a sigla do estado com 2 letras.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.warning({
        title: "Revise os dados",
        message: "Existem campos que precisam da sua atenção.",
      });

      window.setTimeout(() => {
        const firstInvalidField = document.querySelector(
          ".cadastro-detalhes-field--error input"
        );

        firstInvalidField?.focus();
        firstInvalidField?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 80);

      return false;
    }

    if (!sessionId) {
      toast.error({
        title: "Sessão de cadastro inválida",
        message:
          "Volte à etapa anterior e reinicie o processo de cadastro.",
        duration: 0,
      });

      return false;
    }

    return true;
  };

  const atualizarDetalhesCadastro = async () => {
    if (!sessionId) return false;

    const url = `${API_BASE_URL}/psy/temp-register/${sessionId}`;

    const body = {
      nomeUsuario: nome.trim(),
      crp: formatCrpForPayload(crp),
      cep: formatCepInput(cep),
      endereco: endereco.trim(),
      bairro: bairro.trim(),
      cidade: cidade.trim(),
      estado: uf.trim().toUpperCase(),
      pais: pais.trim() || "Brasil",
      telefone,
      nascimento,
      valorPlano: planPrice,
      plano: planName,
      fcmToken: null,
    };

    try {
      const response = await axios.put(url, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const success =
        response.status === 200 || response.status === 201;

      if (success) {
        persistRegistrationFlow({
          detalhesSalvos: true,
        });
      }

      return success;
    } catch (error) {
      console.error("Erro ao atualizar detalhes:", error);

      toast.error({
        title: "Não foi possível salvar seus dados",
        message: getErrorMessage(
          error,
          "O servidor não concluiu a atualização do cadastro."
        ),
      });

      return false;
    }
  };

  const enviarCodigoConfirmacao = async ({
    isResend = false,
  } = {}) => {
    if (!sessionId || !email) {
      toast.error({
        title: "Cadastro incompleto",
        message:
          "Não foi possível identificar a sessão ou o e-mail do cadastro.",
        duration: 0,
      });

      return false;
    }

    if (isResend && resendCooldown > 0) return false;

    setEnviandoCodigo(true);

    try {
      const url =
        `${API_BASE_URL}/psy/temp-register/` +
        `${sessionId}/email/enviar-codigo`;

      const response = await axios.post(
        url,
        {
          email,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const success =
        response.status >= 200 && response.status < 300;

      if (!success) {
        toast.error({
          title: "Código não enviado",
          message:
            "O servidor não concluiu o envio do código de confirmação.",
        });

        return false;
      }

      setCodigoEmail("");
      setErrors((current) => ({
        ...current,
        codigo: "",
      }));
      setEtapa("confirmarEmail");
      setResendCooldown(60);

      persistRegistrationFlow({
        etapaCadastro: "confirmarEmail",
      });

      toast.success({
        title: isResend ? "Novo código enviado" : "Código enviado",
        message: `Enviamos um código de 6 dígitos para ${email}.`,
      });

      return true;
    } catch (error) {
      console.error("Erro ao enviar código de confirmação:", error);

      toast.error({
        title: "Não foi possível enviar o código",
        message: getErrorMessage(
          error,
          "Verifique sua conexão e tente novamente."
        ),
      });

      return false;
    } finally {
      setEnviandoCodigo(false);
    }
  };

  const navegarParaPagamento = () => {
    const flow = persistRegistrationFlow({
      emailConfirmado: true,
      etapaCadastro: "pagamento",
    });

    navigate("/payment", {
      replace: true,
      state: {
        ...flow,
        crp: formatCrpForPayload(crp),
      },
    });
  };

  const confirmarCodigoEmail = async () => {
    const codigo = normalizeCodigo(codigoEmail);

    if (codigo.length !== 6) {
      setErrors((current) => ({
        ...current,
        codigo: "Preencha os 6 dígitos do código recebido.",
      }));

      return;
    }

    if (!sessionId || !email) {
      toast.error({
        title: "Cadastro inválido",
        message:
          "Volte à etapa anterior e reinicie o processo de cadastro.",
        duration: 0,
      });

      return;
    }

    setConfirmandoCodigo(true);

    try {
      const url =
        `${API_BASE_URL}/psy/temp-register/` +
        `${sessionId}/email/confirmar-codigo`;

      const response = await axios.post(
        url,
        {
          email,
          codigo,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success({
          title: "E-mail confirmado",
          message:
            "Sua identidade digital foi validada. Avançando para o pagamento.",
        });

        navegarParaPagamento();
        return;
      }

      setErrors((current) => ({
        ...current,
        codigo: "Código inválido ou expirado.",
      }));
    } catch (error) {
      console.error("Erro ao confirmar código:", error);

      setErrors((current) => ({
        ...current,
        codigo: getErrorMessage(
          error,
          "Código inválido ou expirado."
        ),
      }));

      toast.error({
        title: "Não foi possível confirmar",
        message: getErrorMessage(
          error,
          "Revise o código recebido ou solicite um novo."
        ),
      });
    } finally {
      setConfirmandoCodigo(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading || !validateDetails()) return;

    setSalvandoDetalhes(true);

    try {
      const saved = await atualizarDetalhesCadastro();

      if (!saved) return;

      await enviarCodigoConfirmacao();
    } finally {
      setSalvandoDetalhes(false);
    }
  };

  const handleBack = () => {
    if (loading) return;

    if (etapa === "confirmarEmail") {
      setEtapa("detalhes");
      setCodigoEmail("");
      setErrors((current) => ({
        ...current,
        codigo: "",
      }));
      return;
    }

    navigate(-1);
  };

  const handleCorrectEmail = () => {
    if (loading) return;

    persistRegistrationFlow({
      etapaCadastro: "dadosAcesso",
    });

    navigate(-1);
  };

  const etapaTitle =
    etapa === "confirmarEmail"
      ? "Confirme seu e-mail"
      : "Complete seu perfil profissional";

  const etapaDescription =
    etapa === "confirmarEmail"
      ? "Esta validação protege sua conta e garante que as comunicações sejam entregues corretamente."
      : "Essas informações estruturam seu perfil e personalizam os fluxos iniciais do PsyAssist Pro.";

  if (!flowLoaded) {
    return (
      <main className="cadastro-detalhes-page">
        <div className="cadastro-detalhes-loading">
          <LoaderCircle size={26} />
          <span>Carregando seu cadastro...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="cadastro-detalhes-page">
      <div className="cadastro-detalhes-background" aria-hidden="true">
        <span className="cadastro-detalhes-background__orb cadastro-detalhes-background__orb--one" />
        <span className="cadastro-detalhes-background__orb cadastro-detalhes-background__orb--two" />
        <span className="cadastro-detalhes-background__grid" />
      </div>

      <div className="cadastro-detalhes-layout">
        <aside className="cadastro-detalhes-aside">
          <div className="cadastro-detalhes-brand">
            <img src={Logo} alt="PsyAssist Pro" />

            <div>
              <strong>PsyAssist Pro</strong>
              <span>Onboarding inteligente</span>
            </div>
          </div>

          <div className="cadastro-detalhes-aside__content">
            <span className="cadastro-detalhes-kicker">
              <Sparkles size={15} />
              Configuração assistida
            </span>

            <h1>Uma base bem configurada reduz trabalho em toda a operação.</h1>

            <p>
              A PSYA utiliza essas informações para organizar sua experiência
              inicial, contextualizar recursos e reduzir preenchimentos futuros.
            </p>

            <div className="cadastro-detalhes-steps">
              <div className="cadastro-detalhes-step cadastro-detalhes-step--complete">
                <span>
                  <Check size={16} />
                </span>

                <div>
                  <strong>Dados de acesso</strong>
                  <small>E-mail e senha definidos</small>
                </div>
              </div>

              <div
                className={`cadastro-detalhes-step ${
                  etapa === "detalhes"
                    ? "cadastro-detalhes-step--active"
                    : "cadastro-detalhes-step--complete"
                }`}
              >
                <span>
                  {etapa === "detalhes" ? (
                    <BriefcaseBusiness size={16} />
                  ) : (
                    <Check size={16} />
                  )}
                </span>

                <div>
                  <strong>Perfil profissional</strong>
                  <small>Identificação e endereço</small>
                </div>
              </div>

              <div
                className={`cadastro-detalhes-step ${
                  etapa === "confirmarEmail"
                    ? "cadastro-detalhes-step--active"
                    : ""
                }`}
              >
                <span>
                  <MailCheck size={16} />
                </span>

                <div>
                  <strong>Verificação do e-mail</strong>
                  <small>Proteção da identidade digital</small>
                </div>
              </div>

              <div className="cadastro-detalhes-step">
                <span>
                  <CreditCard size={16} />
                </span>

                <div>
                  <strong>Pagamento</strong>
                  <small>Última etapa da ativação</small>
                </div>
              </div>
            </div>
          </div>

          <div className="cadastro-detalhes-plan-card">
            <div className="cadastro-detalhes-plan-card__top">
              <span>{userTypeLabel}</span>
              <CheckCircle2 size={18} />
            </div>

            <strong>{planName}</strong>
            <p>{planPrice}</p>

            <small>
              O plano foi preservado durante o cadastro e seguirá para a etapa
              segura de pagamento.
            </small>
          </div>
        </aside>

        <section className="cadastro-detalhes-panel">
          <div className="cadastro-detalhes-progress">
            <div className="cadastro-detalhes-progress__header">
              <span>
                {etapa === "confirmarEmail"
                  ? "Verificação final"
                  : "Perfil profissional"}
              </span>

              <strong>{progressValue}% concluído</strong>
            </div>

            <div
              className="cadastro-detalhes-progress__track"
              role="progressbar"
              aria-label="Progresso do cadastro"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progressValue}
            >
              <span style={{ width: `${progressValue}%` }} />
            </div>
          </div>

          <header className="cadastro-detalhes-header">
            <div className="cadastro-detalhes-header__icon">
              {etapa === "confirmarEmail" ? (
                <MailCheck size={22} />
              ) : (
                <BadgeCheck size={22} />
              )}
            </div>

            <div>
              <span>
                {etapa === "confirmarEmail"
                  ? "Segurança da conta"
                  : "Identidade profissional"}
              </span>

              <h2>{etapaTitle}</h2>
              <p>{etapaDescription}</p>
            </div>
          </header>

          {!flowValid ? (
            <section
              className="cadastro-detalhes-flow-alert"
              role="alert"
            >
              <div>
                <LockKeyhole size={20} />
              </div>

              <div>
                <strong>Fluxo de cadastro incompleto</strong>
                <p>
                  Não foi possível recuperar a sessão ou o e-mail informado na
                  etapa anterior.
                </p>
              </div>

              <button type="button" onClick={() => navigate(-1)}>
                Retomar cadastro
              </button>
            </section>
          ) : etapa === "detalhes" ? (
            <form
              className="cadastro-detalhes-form"
              onSubmit={handleSubmit}
              noValidate
            >
              <section className="cadastro-detalhes-form-card">
                <div className="cadastro-detalhes-form-card__header">
                  <div>
                    <span>
                      <UserRound size={16} />
                    </span>

                    <div>
                      <strong>Identificação profissional</strong>
                      <small>
                        Dados utilizados no seu perfil e documentos da
                        plataforma.
                      </small>
                    </div>
                  </div>

                  <em>Campos com * são obrigatórios</em>
                </div>

                <div className="cadastro-detalhes-form-grid">
                  <FormField
                    id="cadastro-detalhes-nome"
                    label="Nome completo"
                    error={errors.nome}
                    required
                    className="cadastro-detalhes-field--wide"
                  >
                    <div className="cadastro-detalhes-control">
                      <UserRound size={18} />

                      <input
                        id="cadastro-detalhes-nome"
                        type="text"
                        value={nome}
                        onChange={(event) => {
                          setNome(event.target.value);
                          clearFieldError("nome");
                        }}
                        placeholder="Digite seu nome completo"
                        autoComplete="name"
                        disabled={loading}
                        aria-invalid={Boolean(errors.nome)}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-crp"
                    label="CRP"
                    hint="Formato 00/000000"
                    error={errors.crp}
                    required
                  >
                    <div className="cadastro-detalhes-control">
                      <BadgeCheck size={18} />

                      <input
                        id="cadastro-detalhes-crp"
                        type="text"
                        inputMode="numeric"
                        value={crp}
                        onChange={(event) => {
                          setCrp(formatCrpForInput(event.target.value));
                          clearFieldError("crp");
                        }}
                        placeholder="00/000000"
                        autoComplete="off"
                        disabled={loading}
                        aria-invalid={Boolean(errors.crp)}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-email"
                    label="E-mail de acesso"
                    hint="Definido na etapa anterior"
                    error={errors.email}
                  >
                    <div className="cadastro-detalhes-control cadastro-detalhes-control--readonly">
                      <Mail size={18} />

                      <input
                        id="cadastro-detalhes-email"
                        type="email"
                        value={email}
                        disabled
                        readOnly
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-telefone"
                    label="Telefone"
                    hint="Opcional"
                    error={errors.telefone}
                  >
                    <div className="cadastro-detalhes-control">
                      <Phone size={18} />

                      <input
                        id="cadastro-detalhes-telefone"
                        type="tel"
                        inputMode="tel"
                        value={telefone}
                        onChange={(event) => {
                          setTelefone(formatPhoneInput(event.target.value));
                          clearFieldError("telefone");
                        }}
                        placeholder="(11) 99999-9999"
                        autoComplete="tel"
                        disabled={loading}
                        aria-invalid={Boolean(errors.telefone)}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-nascimento"
                    label="Data de nascimento"
                    hint="Opcional"
                    error={errors.nascimento}
                  >
                    <div className="cadastro-detalhes-control">
                      <CalendarDays size={18} />

                      <input
                        id="cadastro-detalhes-nascimento"
                        type="text"
                        inputMode="numeric"
                        value={nascimento}
                        onChange={(event) => {
                          setNascimento(formatDateInput(event.target.value));
                          clearFieldError("nascimento");
                        }}
                        placeholder="dd/mm/aaaa"
                        autoComplete="bday"
                        disabled={loading}
                        aria-invalid={Boolean(errors.nascimento)}
                      />
                    </div>
                  </FormField>
                </div>
              </section>

              <section className="cadastro-detalhes-form-card">
                <div className="cadastro-detalhes-form-card__header">
                  <div>
                    <span>
                      <MapPin size={16} />
                    </span>

                    <div>
                      <strong>Endereço profissional</strong>
                      <small>
                        O CEP preenche automaticamente os dados disponíveis.
                      </small>
                    </div>
                  </div>

                  {buscandoCep ? (
                    <em className="cadastro-detalhes-cep-status">
                      <LoaderCircle size={14} />
                      Consultando CEP
                    </em>
                  ) : cepEncontrado ? (
                    <em className="cadastro-detalhes-cep-status cadastro-detalhes-cep-status--success">
                      <CheckCircle2 size={14} />
                      Endereço localizado
                    </em>
                  ) : null}
                </div>

                <div className="cadastro-detalhes-form-grid">
                  <FormField
                    id="cadastro-detalhes-cep"
                    label="CEP"
                    error={errors.cep}
                    required
                  >
                    <div className="cadastro-detalhes-control">
                      <MapPin size={18} />

                      <input
                        id="cadastro-detalhes-cep"
                        type="text"
                        inputMode="numeric"
                        value={cep}
                        onChange={(event) => {
                          setCep(formatCepInput(event.target.value));
                          clearFieldError("cep");
                        }}
                        placeholder="00000-000"
                        autoComplete="postal-code"
                        disabled={loading}
                        aria-invalid={Boolean(errors.cep)}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-endereco"
                    label="Endereço"
                    className="cadastro-detalhes-field--wide"
                  >
                    <div className="cadastro-detalhes-control">
                      <Building2 size={18} />

                      <input
                        id="cadastro-detalhes-endereco"
                        type="text"
                        value={endereco}
                        onChange={(event) =>
                          setEndereco(event.target.value)
                        }
                        placeholder="Rua, avenida e número"
                        autoComplete="street-address"
                        disabled={loading}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-bairro"
                    label="Bairro"
                  >
                    <div className="cadastro-detalhes-control">
                      <Building2 size={18} />

                      <input
                        id="cadastro-detalhes-bairro"
                        type="text"
                        value={bairro}
                        onChange={(event) => setBairro(event.target.value)}
                        placeholder="Bairro"
                        autoComplete="address-level3"
                        disabled={loading}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-cidade"
                    label="Cidade"
                  >
                    <div className="cadastro-detalhes-control">
                      <Building2 size={18} />

                      <input
                        id="cadastro-detalhes-cidade"
                        type="text"
                        value={cidade}
                        onChange={(event) => setCidade(event.target.value)}
                        placeholder="Cidade"
                        autoComplete="address-level2"
                        disabled={loading}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-uf"
                    label="UF"
                    error={errors.uf}
                  >
                    <div className="cadastro-detalhes-control">
                      <MapPin size={18} />

                      <input
                        id="cadastro-detalhes-uf"
                        type="text"
                        value={uf}
                        maxLength={2}
                        onChange={(event) => {
                          setUf(event.target.value.toUpperCase());
                          clearFieldError("uf");
                        }}
                        placeholder="SP"
                        autoComplete="address-level1"
                        disabled={loading}
                        aria-invalid={Boolean(errors.uf)}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id="cadastro-detalhes-pais"
                    label="País"
                  >
                    <div className="cadastro-detalhes-control">
                      <MapPin size={18} />

                      <input
                        id="cadastro-detalhes-pais"
                        type="text"
                        value={pais}
                        onChange={(event) => setPais(event.target.value)}
                        placeholder="Brasil"
                        autoComplete="country-name"
                        disabled={loading}
                      />
                    </div>
                  </FormField>
                </div>
              </section>

              <section className="cadastro-detalhes-security-note">
                <ShieldCheck size={20} />

                <p>
                  <strong>Seus dados profissionais permanecem protegidos.</strong>
                  Eles serão utilizados para configurar sua conta e os recursos
                  operacionais do PsyAssist Pro.
                </p>
              </section>

              <div className="cadastro-detalhes-actions">
                <button
                  type="button"
                  className="cadastro-detalhes-secondary-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <ArrowLeft size={18} />
                  Voltar
                </button>

                <button
                  type="submit"
                  className="cadastro-detalhes-primary-btn"
                  disabled={loading || !flowValid}
                >
                  {salvandoDetalhes || enviandoCodigo ? (
                    <>
                      <LoaderCircle
                        size={18}
                        className="cadastro-detalhes-spin"
                      />
                      Preparando verificação...
                    </>
                  ) : (
                    <>
                      Enviar código de verificação
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <section className="cadastro-detalhes-confirmation">
              <div className="cadastro-detalhes-confirmation__visual">
                <div className="cadastro-detalhes-confirmation__icon">
                  <MailCheck size={30} />
                </div>

                <span className="cadastro-detalhes-confirmation__pulse" />
              </div>

              <div className="cadastro-detalhes-confirmation__copy">
                <span>Verificação de segurança</span>
                <h3>Digite o código enviado para seu e-mail</h3>

                <p>
                  Enviamos um código de 6 dígitos para
                  <strong> {email}</strong>. Ele expira por segurança.
                </p>
              </div>

              <div className="cadastro-detalhes-confirmation__code">
                <label>Código de confirmação</label>

                <OtpInput
                  value={codigoEmail}
                  onChange={(value) => {
                    setCodigoEmail(value);
                    clearFieldError("codigo");
                  }}
                  disabled={confirmandoCodigo}
                  error={errors.codigo}
                />

                {errors.codigo ? (
                  <p role="alert">{errors.codigo}</p>
                ) : (
                  <small>
                    Você pode colar o código completo diretamente no primeiro
                    campo.
                  </small>
                )}
              </div>

              <div className="cadastro-detalhes-confirmation__actions">
                <button
                  type="button"
                  className="cadastro-detalhes-primary-btn"
                  onClick={confirmarCodigoEmail}
                  disabled={
                    confirmandoCodigo ||
                    codigoEmail.length !== 6
                  }
                >
                  {confirmandoCodigo ? (
                    <>
                      <LoaderCircle
                        size={18}
                        className="cadastro-detalhes-spin"
                      />
                      Confirmando...
                    </>
                  ) : (
                    <>
                      Confirmar e ir para pagamento
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="cadastro-detalhes-secondary-btn"
                  onClick={() =>
                    enviarCodigoConfirmacao({
                      isResend: true,
                    })
                  }
                  disabled={
                    enviandoCodigo ||
                    confirmandoCodigo ||
                    resendCooldown > 0
                  }
                >
                  <RefreshCw
                    size={17}
                    className={
                      enviandoCodigo
                        ? "cadastro-detalhes-spin"
                        : ""
                    }
                  />

                  {enviandoCodigo
                    ? "Reenviando..."
                    : resendCooldown > 0
                      ? `Reenviar em ${resendCooldown}s`
                      : "Reenviar código"}
                </button>
              </div>

              <div className="cadastro-detalhes-confirmation__links">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <ArrowLeft size={15} />
                  Revisar dados profissionais
                </button>

                <button
                  type="button"
                  onClick={handleCorrectEmail}
                  disabled={loading}
                >
                  Corrigir e-mail
                </button>
              </div>

              <section className="cadastro-detalhes-confirmation__security">
                <ShieldCheck size={19} />

                <p>
                  O PsyAssist Pro nunca solicitará este código por telefone,
                  mensagem ou atendimento externo.
                </p>
              </section>
            </section>
          )}

          <footer className="cadastro-detalhes-footer">
            <span>Ambiente seguro</span>
            <span aria-hidden="true">•</span>
            <span>Verificação protegida</span>
            <span aria-hidden="true">•</span>
            <span>Suporte PsyAssist</span>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default CadastroDetalhesScreen;
